/**
 * EditorJS inline tool that wraps the current selection in
 * `<span style="color:#RRGGBB">…</span>`.
 *
 * Pattern matches the existing ReferenceTool. The color picker UI is
 * portalled via `createFloatingPalette` (see that module for why: EditorJS's
 * own popover clips a nested absolutely-positioned child and reentrantly
 * re-toggles it on every click inside its subtree).
 *
 * The pure color-application logic + palette live in
 * `apps/web/src/lib/text-color-ops.ts` so the React `useSelectionToolbar`
 * (used on container titles) can share the exact same output shape.
 */
import { BrandColors } from '@/lib/brand-colors';
import { SPAN_SANITIZE } from '@/editorjs-tools/sanitize-config';
import { createFloatingPalette, createPaletteGestureHandlers, applyFloatingPaletteChrome, type FloatingPalette, type PaletteGestureHandlers } from '@/editorjs-tools/floating-palette';
import {
  DEFAULT_COLOR_PALETTE,
  applyColorToRange,
  getLastPickedColor,
  getSelectionColor,
  isCaretInsideColoredSpan,
  removeColorFromRange,
} from '@/lib/text-color-ops';

const DEFAULT_SWATCH_BACKGROUND = '#28a745';

export class ColorTool {
  static isInline = true as const;
  static sanitize = { span: SPAN_SANITIZE };
  static title = 'Color';

  private wrapper: HTMLElement | null = null;
  private swatch: HTMLElement | null = null;
  private currentRange: Range | null = null;
  private palette: FloatingPalette | null = null;
  private paletteGesture: PaletteGestureHandlers | null = null;

  render(): HTMLElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.classList.add('ce-inline-tool', 'ce-inline-tool--color');
    button.title = 'Text color';
    const swatch = document.createElement('span');
    swatch.setAttribute(
      'style',
      `display:inline-block;width:14px;height:14px;border:1px solid #999;background:${DEFAULT_SWATCH_BACKGROUND}`,
    );
    button.appendChild(swatch);
    this.wrapper = button;
    this.swatch = swatch;
    this.paletteGesture = createPaletteGestureHandlers(
      () => this.getOrCreatePalette(),
    );
    button.addEventListener('mousedown', e => {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0 && !sel.isCollapsed) {
        this.currentRange = sel.getRangeAt(0).cloneRange();
      }
      this.paletteGesture?.onToolButtonMouseDown(e);
    });
    return button;
  }

  surround(range: Range): void {
    this.currentRange = range.cloneRange();
    this.paletteGesture?.onSurroundPalette();
  }

  clear(): void {
    // EditorJS calls `clear()` when the inline toolbar closes — that often
    // happens immediately after clicking our button (selection collapses /
    // focus leaves the contenteditable). Do NOT close the floating palette
    // here; it owns its own dismissal (Confirm / outside click / Escape).
    // Swatch handlers remain on the detached tool instance via closure.
    this.palette = null;
    this.paletteGesture?.resetGestureState();
  }

  // EditorJS calls checkState() on every selection change inside the
  // editor. We piggy-back to keep the swatch in sync with whatever color
  // is currently at the caret (or, failing that, the last-picked color).
  checkState(): boolean {
    this.updateSwatch();
    return isCaretInsideColoredSpan();
  }

  /** Public for testing. Wraps the range. Returns false on invalid color. */
  applyColor(range: Range, color: string): boolean {
    const ok = applyColorToRange(range, color);
    if (ok) {
      this.updateSwatch();
      this.refreshRangeFromLiveSelection();
    }
    return ok;
  }

  private updateSwatch(): void {
    if (!this.swatch) return;
    const active =
      getSelectionColor() ?? getLastPickedColor() ?? DEFAULT_SWATCH_BACKGROUND;
    this.swatch.style.background = active;
  }

  // `applyColorToRange`/`removeColorFromRange` collapse whatever range
  // they're called on (`Range#extractContents` side effect) and reselect the
  // mutated contents as the live browser selection. Re-derive our cached
  // range from that live selection so the NEXT swatch/reset/picker click in
  // the same palette session acts on the correct (non-collapsed) range
  // instead of a stale, now-empty one.
  private refreshRangeFromLiveSelection(): void {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      this.currentRange = sel.getRangeAt(0);
    }
  }

  private getOrCreatePalette(): FloatingPalette {
    if (this.palette) return this.palette;
    const anchor = this.wrapper;
    if (!anchor) throw new Error('ColorTool.render() must run before surround()');
    const palette = createFloatingPalette(anchor);
    applyFloatingPaletteChrome(palette.element);

    // Row 1 — preset swatches.
    const swatchRow = document.createElement('div');
    swatchRow.setAttribute('style', 'display:flex;gap:4px');
    for (const c of DEFAULT_COLOR_PALETTE) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.setAttribute('aria-label', `Apply color ${c}`);
      dot.title = c;
      dot.setAttribute(
        'style',
        `width:20px;height:20px;border:1px solid #c8ccd2;border-radius:50%;background:${c};cursor:pointer;padding:0`,
      );
      dot.addEventListener('mousedown', e => {
        e.preventDefault();
        if (this.currentRange) this.applyColor(this.currentRange, c);
        // Palette stays open — explicit Confirm below closes it.
      });
      swatchRow.appendChild(dot);
    }
    palette.element.appendChild(swatchRow);

    // Row 2 — actions (reset + custom picker + confirm).
    const actionsRow = document.createElement('div');
    actionsRow.setAttribute(
      'style',
      'display:flex;gap:6px;align-items:center;border-top:1px solid #eef0f3;padding-top:6px',
    );

    const resetBtn = document.createElement('button');
    resetBtn.type = 'button';
    resetBtn.setAttribute('aria-label', 'Reset color');
    resetBtn.title = 'Reset color';
    resetBtn.setAttribute(
      'style',
      'width:24px;height:24px;border:1px solid #c8ccd2;border-radius:4px;background:white;color:#1d2128;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;padding:0',
    );
    resetBtn.textContent = '⌀';
    resetBtn.addEventListener('mousedown', e => {
      e.preventDefault();
      const range = this.currentRange;
      if (range) {
        const removed = removeColorFromRange(range);
        if (removed) {
          this.updateSwatch();
          this.refreshRangeFromLiveSelection();
        }
      }
      // Palette stays open.
    });
    actionsRow.appendChild(resetBtn);

    const pickerWrap = document.createElement('label');
    pickerWrap.title = 'Custom color';
    pickerWrap.setAttribute(
      'style',
      'position:relative;width:24px;height:24px;border:1px solid #c8ccd2;border-radius:4px;background:white;color:#1d2128;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;padding:0;font-weight:700',
    );
    pickerWrap.textContent = '＋';

    const pickerInput = document.createElement('input');
    pickerInput.type = 'color';
    pickerInput.setAttribute('aria-label', 'Custom color');
    pickerInput.setAttribute(
      'style',
      'position:absolute;inset:0;width:100%;height:100%;opacity:0;cursor:pointer;border:0;padding:0',
    );
    pickerInput.addEventListener('mousedown', e => {
      e.stopPropagation();
    });
    pickerInput.addEventListener('change', () => {
      if (this.currentRange) this.applyColor(this.currentRange, pickerInput.value);
      // Palette stays open so the user can keep iterating.
    });
    pickerWrap.appendChild(pickerInput);
    actionsRow.appendChild(pickerWrap);

    const confirmBtn = document.createElement('button');
    confirmBtn.type = 'button';
    confirmBtn.setAttribute('aria-label', 'Confirm color selection');
    confirmBtn.title = 'Confirm';
    confirmBtn.setAttribute(
      'style',
      `margin-left:auto;width:24px;height:24px;border:1px solid ${BrandColors.purple[950]};border-radius:4px;background:${BrandColors.purple[950]};color:white;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;padding:0;font-weight:700`,
    );
    confirmBtn.textContent = '✓';
    confirmBtn.addEventListener('mousedown', e => {
      e.preventDefault();
      palette.close();
    });
    actionsRow.appendChild(confirmBtn);

    palette.element.appendChild(actionsRow);

    this.palette = palette;
    return palette;
  }
}
